import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

type JsonValue = Record<string, unknown> | string | number | boolean | null;

// On native, mirror auth data to Capacitor Preferences (Android SharedPreferences)
// so it survives WebView localStorage being cleared on app restart.
// `cc_refresh` (the refresh token) is mirrored on native only — on web the refresh
// token lives in an HttpOnly cookie and is never written to localStorage.
const AUTH_KEYS = ['token', 'user', 'accountType', 'organization', 'membership', 'cc_refresh'] as const;

// localStorage key holding why the last session ended, read once by the login
// page to show the right banner ("expired" vs a plain sign-out).
const SESSION_END_REASON_KEY = 'sessionEndReason';
const REFRESH_TOKEN_KEY = 'cc_refresh';

export type SessionEndReason = 'expired' | 'session_ended' | 'deactivated' | 'manual' | 'unknown';

const persistToNative = async () => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    for (const key of AUTH_KEYS) {
      const value = localStorage.getItem(key);
      if (value !== null) {
        await Preferences.set({ key, value });
      } else {
        await Preferences.remove({ key });
      }
    }
  } catch {
    // Preferences plugin may not be available
  }
};

/** Call once on app startup to restore auth from native storage if localStorage was cleared. */
export const restoreAuthFromNative = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    if (localStorage.getItem('token')) return false;
    const { value: token } = await Preferences.get({ key: 'token' });
    if (!token) return false;
    for (const key of AUTH_KEYS) {
      const { value } = await Preferences.get({ key });
      if (value !== null) {
        localStorage.setItem(key, value);
      }
    }
    return true;
  } catch {
    return false;
  }
};

export interface AuthSessionData {
  token: string;
  user: JsonValue;
  accountType?: string;
  organization?: JsonValue;
  membership?: JsonValue;
  includeRoleFromMembership?: boolean;
  availableViews?: string[];
}

export interface ClearAuthSessionOptions {
  clearAccountType?: boolean;
  clearOrganization?: boolean;
  clearMembership?: boolean;
  dispatchAuthChange?: boolean;
}

const safelyParseJson = <T>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const writeJson = (key: string, value: JsonValue | undefined) => {
  if (value === undefined) return;
  localStorage.setItem(key, JSON.stringify(value));
};

const addRoleFromMembership = (user: JsonValue, membership?: JsonValue) => {
  if (
    !membership ||
    typeof membership !== 'object' ||
    membership === null ||
    typeof user !== 'object' ||
    user === null
  ) {
    return user;
  }
  if (!('role' in membership)) {
    return user;
  }
  const role = (membership as { role?: string }).role || 'user';
  return { ...(user as Record<string, unknown>), role };
};

export const setAuthSession = (data: AuthSessionData, dispatchAuthChange = false) => {
  const userWithRole = data.includeRoleFromMembership
    ? addRoleFromMembership(data.user, data.membership)
    : data.user;

  localStorage.setItem('token', data.token);
  writeJson('user', userWithRole);
  writeJson('organization', data.organization);
  writeJson('membership', data.membership);
  if (data.accountType) {
    localStorage.setItem('accountType', data.accountType);
  }
  if (data.availableViews && Array.isArray(data.availableViews)) {
    localStorage.setItem('availableViews', JSON.stringify(data.availableViews));
  }
  if (dispatchAuthChange) {
    window.dispatchEvent(new Event('authChange'));
  }
  persistToNative();
  return userWithRole;
};

export const clearAuthSession = ({
  clearAccountType = false,
  clearOrganization = false,
  clearMembership = false,
  dispatchAuthChange = false,
}: ClearAuthSessionOptions = {}) => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  // Native refresh token (no-op on web, where it's an HttpOnly cookie).
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  // availableViews is session metadata derived from the login response; always
  // clear it on auth-session clear so a fresh login repopulates it cleanly.
  localStorage.removeItem('availableViews');
  if (clearAccountType) {
    localStorage.removeItem('accountType');
  }
  if (clearOrganization) {
    localStorage.removeItem('organization');
  }
  if (clearMembership) {
    localStorage.removeItem('membership');
  }
  if (dispatchAuthChange) {
    window.dispatchEvent(new Event('authChange'));
  }
  // Always signal logout (independent of the optional authChange UI signal) so
  // platform layers can wipe persisted user data. The web app listens for this
  // to clear its offline IndexedDB cache — see lib/offline/authCacheReset.ts.
  window.dispatchEvent(new Event('auth:cleared'));
  persistToNative();
};

export const getStoredUser = <T = Record<string, unknown>>(): T | null =>
  safelyParseJson<T>(localStorage.getItem('user'));

export const getAvailableViews = (): string[] => {
  const raw = localStorage.getItem('availableViews');
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

// ── Refresh token (native only) ──────────────────────────────────────────────
// Web keeps the refresh token in an HttpOnly cookie; these are no-ops there.
export const getRefreshToken = (): string | null =>
  Capacitor.isNativePlatform() ? localStorage.getItem(REFRESH_TOKEN_KEY) : null;

export const setRefreshToken = (token: string | null) => {
  if (!Capacitor.isNativePlatform()) return;
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
  persistToNative();
};

/** Overwrite just the access token after a silent refresh (keeps the rest of the session). */
export const setAccessToken = (token: string) => {
  localStorage.setItem('token', token);
  persistToNative();
};

// ── Access-token expiry (client-side, no network) ────────────────────────────
/** Decode a JWT's `exp` (ms since epoch) without any dependency. Returns null if unreadable. */
export const getTokenExpiryMs = (token: string | null = localStorage.getItem('token')): number | null => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

// ── One session-end funnel ───────────────────────────────────────────────────
// Everything that ends a session (401 interceptor, idle timer, manual logout)
// goes through endSession() so behavior is identical. The platform layer (web /
// citizen) registers a handler that performs the actual navigation to its login
// route; endSession clears state, records the reason, and invokes it.
export interface SessionEndContext {
  /** accountType as it was just before the session was cleared (for login routing). */
  accountTypeBefore: string | null;
}
type SessionEndHandler = (reason: SessionEndReason, ctx: SessionEndContext) => void;
let sessionEndHandler: SessionEndHandler | null = null;

export const registerSessionEndHandler = (handler: SessionEndHandler) => {
  sessionEndHandler = handler;
};

export const getSessionEndReason = (): SessionEndReason | null =>
  (localStorage.getItem(SESSION_END_REASON_KEY) as SessionEndReason | null) || null;

export const clearSessionEndReason = () => localStorage.removeItem(SESSION_END_REASON_KEY);

let endingSession = false;

/**
 * End the current session exactly once. Records why (for the login banner), wipes
 * auth state + offline cache (via clearAuthSession's `auth:cleared` event), and
 * hands off to the registered platform handler for navigation. Guarded so N
 * parallel 401s produce a single logout, not a redirect storm.
 */
export const endSession = (
  reason: SessionEndReason = 'unknown',
  options: ClearAuthSessionOptions = {},
) => {
  if (endingSession) return;
  endingSession = true;
  const accountTypeBefore = localStorage.getItem('accountType');
  try {
    localStorage.setItem(SESSION_END_REASON_KEY, reason);
  } catch {
    // storage may be unavailable; proceed with logout regardless
  }
  clearAuthSession(options);
  if (sessionEndHandler) {
    sessionEndHandler(reason, { accountTypeBefore });
  }
};

/** Reset the once-only guard — call after a successful fresh login so a later expiry logs out again. */
export const resetSessionEndGuard = () => {
  endingSession = false;
  clearSessionEndReason();
};
